import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EMPTY, Subscription, catchError, combineLatest, delay, distinctUntilChanged, filter, map, take } from 'rxjs';
import { AuthService, FramesService } from '@app/core/services';
import { ConfigService } from '@app/core/services/config/config.service';
import { ClaimsEnum } from '@app/enums/claims.enum';
import { ConfigEnum } from '@app/enums/config.enum';
import { LoadingService } from '@app/shared/services/loading.service';
import { LoginDialogComponent } from './components/login-dialog.component';

interface LoginState {
  isEnabled: boolean;
  auth0User: any;
  framesUser: any;
  hasToken: boolean;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private framesService: FramesService,
    private http: HttpClient,
    private configService: ConfigService,
    private loadingService: LoadingService,
  ) {}

  loginDialog: MatDialogRef<LoginDialogComponent, any> | undefined;
  private subscription = new Subscription();
  private loginInProgress = false;
  private selectedErpUserId: string | null = null;

  ngOnInit(): void {
    // Single state-driven subscription that orchestrates the entire login flow
    const state$ = combineLatest([
      this.authService.isEnabled$,
      this.authService.auth0User$,
      this.authService.framesUser$,
      this.authService.hasAccessToken$,
    ]).pipe(
      map(
        ([isEnabled, auth0User, framesUser, hasToken]) =>
          ({
            isEnabled,
            auth0User,
            framesUser,
            hasToken,
          }) as LoginState,
      ),
      distinctUntilChanged(
        (prev, curr) =>
          prev.isEnabled === curr.isEnabled &&
          prev.auth0User?.id === curr.auth0User?.id &&
          prev.framesUser?.id === curr.framesUser?.id &&
          prev.hasToken === curr.hasToken,
      ),
    );

    this.subscription.add(
      state$.subscribe((state) => {
        this.handleLoginState(state);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.closeDialog();
  }

  private handleLoginState(state: LoginState): void {
    // Auth0 disabled - use native Frames login
    if (!state.isEnabled) {
      this.closeDialog();
      return;
    }

    // No auth0 user - trigger authentication
    if (!state.auth0User) {
      this.authService.authenticate().subscribe();
      return;
    }

    // Already has Frames session - done
    if (state.framesUser) {
      this.closeDialog();
      return;
    }

    // User without Frames session - show dialog to hide old login screen
    if (state.hasToken && !this.loginDialog) {
      this.loginDialog = this.dialog.open(LoginDialogComponent, {
        width: '100vw',
        height: '100vh',
        disableClose: true,
        data: {
          onUserSelectedEvent: this.onUserSelectedEvent,
          isAdmin: state.auth0User.admin,
        },
        panelClass: 'auth0-dialog-panel',
      });

      // Track dialog close events
      this.loginDialog.afterClosed().subscribe(() => {
        document.body.classList.remove('auth0-dialog-open');
      });

      this.loginDialog.afterOpened().subscribe(() => {
        this.protectDialogFromFrames();
        // Add class to hide Frames iframe
        document.body.classList.add('auth0-dialog-open');

        // Automatically proceed with login for non-admin users, or when V4 is enabled,
        // or in production. Admins outside of V4/production fall through to pick from the list.
        const isEnabledV4 = this.authService.getEnabledV4();
        const isProduction = this.configService.get(ConfigEnum.Production);
        const shouldAutoLogin = !state.auth0User.admin || isEnabledV4 || isProduction;
        if (shouldAutoLogin && !this.loginInProgress) {
          this.loginInProgress = true;
          this.login();
        }
      });
    }
  }

  clearFramesLoginForm() {
    const framesLoginInputs = window.$('#morphis-wrapper div.login form input');
    if (framesLoginInputs.length) {
      framesLoginInputs.each(function (index: any, input: any) {
        input.value = '';
      });
    }
  }

  hideFramesLoginForm() {
    const framesLoginForm = window.$('#morphis-wrapper div.login form');
    if (framesLoginForm.length) {
      framesLoginForm.hide();
    }
  }

  closeDialog() {
    document.body.classList.remove('auth0-dialog-open');
    this.dialog.closeAll();
    this.loginDialog = undefined;
  }

  framesLogin() {
    this.clearFramesLoginForm();
    this.framesService.execute('auth0-logged-in');
  }

  logout() {
    this.selectedErpUserId = null; // Clear selected user on logout
    this.framesService.execute('auth0-logged-out');
    this.http.post('oauth/logout', {}).subscribe();
  }

  private postLogin() {
    // Wait for Frames to initialize and establish session via WORKSPACE_INIT
    // before calling oauth/login to ensure session is properly established
    this.framesService.ready$
      .pipe(
        filter((ready) => ready),
        take(1),
      )
      .subscribe(() => {
        this.http
          .post('oauth/login', {})
          .pipe(
            catchError((error: HttpErrorResponse) => {
              let errorMessage = `Unable to authenticate user. Reason: ${error.error}`;
              if (!this.configService.get(ConfigEnum.Auth0ClientId)) {
                errorMessage = 'Unable to obtain client authentication information. Please contact your administrator.';
              }
              if (this.loginDialog) this.loginDialog.componentInstance.errorMessage = errorMessage;
              this.logout();
              return EMPTY;
            }),
          )
          .subscribe((response: any) => {
            if (response?.getNewToken) {
              this.loginInProgress = false;
              this.forceNewToken();
            } else {
              this.framesLogin();

              // Watch for successful Frames workspace load
              const workspaceSub = this.framesService.message$
                .pipe(
                  filter((message) => {
                    const props = message?.header?.[0]?.control?.[0];
                    const block = props?.['@block'];
                    const item = props?.['@item'];
                    const task = props?.['@taskName'];
                    const view = props?.['@view'];
                    return (
                      block == '$MAIN$_BLOCK' &&
                      item == 'MENU_TREE' &&
                      task == 'MAIN' &&
                      view == 'morphis/frames/workspace/ViewWorkspaceWindow'
                    );
                  }),
                  take(1),
                )
                .subscribe(() => {
                  this.closeDialog();
                  this.loginInProgress = false;
                });

              this.subscription.add(workspaceSub);

              // Watch for Frames login errors
              const errorSub = this.framesService.message$
                .pipe(
                  filter((message) => {
                    const props = message?.header?.[0]?.control?.[0];
                    return (
                      props?.['@block'] == 'LOGIN' &&
                      (props?.['@item'] == 'USERNAME' || props?.['@item'] == 'PASSWORD') &&
                      props?.['@taskName'] == 'MAIN' &&
                      props?.['@view'] == 'morphis/frames/workspace/ViewLoginSimple' &&
                      props?.messages?.[0].message?.[0]
                    );
                  }),
                  take(1),
                )
                .subscribe((message) => {
                  const messages = message?.header?.[0]?.control?.[0]?.messages?.[0].message;
                  if (messages && this.loginDialog) {
                    messages.forEach((item: { [x: string]: string }) => {
                      const msg = item['@message_string'].trim();
                      if (msg) {
                        this.loginDialog!.componentInstance.errorMessage = msg;
                        this.loginInProgress = false;
                      }
                    });
                  }
                });

              this.subscription.add(errorSub);
            }
          });
      }); // Close the framesService.ready$ subscribe
  }

  login() {
    this.postLogin();
  }

  onUserSelectedEvent = (erpUserId: string) => {
    this.loadingService.show();
    this.selectedErpUserId = erpUserId;
    this.http
      .patch(`oauth/users/${erpUserId}`, {})
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (this.loginDialog) this.loginDialog.componentInstance.errorMessage = error.error;
          this.logout();
          this.selectedErpUserId = null;
          return EMPTY;
        }),
      )
      .subscribe(() => {
        // Retry fetching token until we get one with the correct user
        this.waitForCorrectToken(erpUserId, 0);
      });
  };

  private waitForCorrectToken(expectedUserId: string, attempt: number): void {
    const maxAttempts = 10;
    const delayMs = 500;

    if (attempt >= maxAttempts) {
      this.loadingService.hide();
      if (this.loginDialog) {
        this.loginDialog.componentInstance.errorMessage = `Unable to authenticate with selected user. Expected '${expectedUserId}' but token contains different user. Please try again.`;
      }
      this.selectedErpUserId = null;
      return;
    }

    // Force a new token from Auth0
    this.authService.forceNewToken();

    // Wait for the new token and validate it
    this.authService.accessToken$.pipe(take(1), delay(delayMs)).subscribe((token) => {
      const tokenUserId = this.extractUserIdFromToken(token);

      // Trim and compare case-insensitively to handle potential formatting differences
      if (tokenUserId && tokenUserId.trim().toLowerCase() === expectedUserId.trim().toLowerCase()) {
        // Token has correct user, proceed with login
        this.selectedErpUserId = null;
        this.loadingService.hide();
        this.loginInProgress = false;
        this.login();
      } else {
        // Token still has wrong user, retry
        this.waitForCorrectToken(expectedUserId, attempt + 1);
      }
    });
  }

  private extractUserIdFromToken(token: string): string | null {
    try {
      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Decode the payload (second part)
      const payload = parts[1];
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(decodedPayload);

      // Extract the ErpUserId claim
      return claims[ClaimsEnum.ErpUserId] || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  private forceNewToken(): void {
    this.authService.forceNewToken();
    // Wait for new token then proceed to login
    this.authService.accessToken$.pipe(take(1)).subscribe(() => {
      if (!this.loginInProgress) {
        this.loginInProgress = true;
        this.login();
      }
    });
  }

  private protectDialogFromFrames(): void {
    // CRITICAL FIX: Prevent Frames jQuery from wiping the dialog
    // Frames uses jQuery .html() to load views, which removes Angular Material's dialog overlay after upgrading to Angular 17
    const jq = (window as any).jQuery || (window as any).$;
    if (jq && jq.fn && jq.fn.html) {
      const originalHtml = jq.fn.html;
      jq.fn.html = function (...args: any[]) {
        // Block .html() calls while dialog is open to protect overlay
        if (args.length > 0 && document.body.classList.contains('auth0-dialog-open')) {
          return this;
        }
        return originalHtml.apply(this, args);
      };
    }
  }
}
